import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Checkbox,
  IconButton,
  Skeleton,
  Paper,
  useMediaQuery,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material/styles';

const DataTable = ({
  columns,
  data,
  loading = false,
  selectable = false,
  selected = [],
  onSelectAll,
  onSelectRow,
  actions,
  onRowClick,
  emptyMessage = 'No records found',
  pagination = true,
  page = 0,
  rowsPerPage = 10,
  totalCount = 0,
  onPageChange,
  onRowsPerPageChange,
  mobileTitleColumn,
  mobileHiddenColumns = [],
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const visibleColumns = columns.filter(
    (col) => !(mobileHiddenColumns || []).includes(col.field)
  );
  const titleCol = mobileTitleColumn
    ? visibleColumns.find((c) => c.field === mobileTitleColumn)
    : visibleColumns[0];
  const contentCols = titleCol
    ? visibleColumns.filter((c) => c.field !== titleCol.field)
    : visibleColumns;

  const renderLoadingRows = () => {
    return [...Array(5)].map((_, index) => (
      <TableRow key={index}>
        {selectable && (
          <TableCell sx={{ padding: '16px' }} padding="checkbox">
            <Skeleton variant="rectangular" width={20} height={20} />
          </TableCell>
        )}
        {columns.map((col, colIndex) => (
          <TableCell key={colIndex} sx={{ padding: '16px' }}>
            <Skeleton variant="text" width={col.width || '100%'} />
          </TableCell>
        ))}
        {actions && (
          <TableCell sx={{ padding: '16px' }} align="right">
            <Skeleton variant="circular" width={32} height={32} />
          </TableCell>
        )}
      </TableRow>
    ));
  };

  const renderEmptyState = () => (
    <TableRow>
      <TableCell
        colSpan={columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0)}
        sx={{
          textAlign: 'center',
          py: 8,
          color: isDark ? '#64748b' : '#7c8fac',
        }}
      >
        {emptyMessage && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: isDark ? '#ffffff' : '#1a202c' }}>
              {emptyMessage}
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: isDark ? '#718096' : '#718096' }}>
              Try adjusting your search or filters
            </Typography>
          </Box>
        )}
      </TableCell>
    </TableRow>
  );

  if (isMobile) {
    return (
      <Box>
        <Box sx={{ borderRadius: '12px', overflow: 'hidden', backgroundColor: isDark ? '#1a1a1a' : '#ffffff', border: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #e2e8f0' }}>
          {loading ? (
            [...Array(5)].map((_, i) => (
              <Box key={i} sx={{ p: 2, borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0' }}>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={16} sx={{ mt: 1 }} />
              </Box>
            ))
          ) : data.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              {emptyMessage && (
                <>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: isDark ? '#fff' : '#1a202c' }}>{emptyMessage}</Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: isDark ? '#718096' : '#718096', mt: 0.5 }}>Try adjusting your search or filters</Typography>
                </>
              )}
            </Box>
          ) : (
            data.map((row, rowIndex) => (
              <Box
                key={row.id || rowIndex}
                onClick={() => onRowClick && onRowClick(row)}
                sx={{
                  p: 2,
                  borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
                  cursor: onRowClick ? 'pointer' : 'default',
                  '&:hover': onRowClick ? { backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc' } : {},
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                {/* Title / primary column – full width, or row with inline columns */}
                {titleCol && (
                  <Box sx={{ mb: contentCols.length > 0 || actions ? 1.5 : 0 }}>
                    {(() => {
                      const inlineWithTitleCols = contentCols.filter((c) => c.mobileInlineWithTitle);
                      const restContentCols = contentCols.filter((c) => !c.mobileInlineWithTitle);
                      if (inlineWithTitleCols.length > 0) {
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {titleCol.render ? titleCol.render(row[titleCol.field], row) : (row[titleCol.field] != null ? String(row[titleCol.field]) : '—')}
                            </Box>
                            <Box sx={{ flexShrink: 0 }}>
                              {inlineWithTitleCols.map((col) => (
                                <Box key={col.field}>
                                  <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: isDark ? '#64748b' : '#94a3b8', mb: 0.25 }}>{col.label}</Typography>
                                  <Box sx={{ fontSize: '0.875rem', color: isDark ? '#e2e8f0' : '#334155' }}>
                                    {col.render ? col.render(row[col.field], row) : (row[col.field] != null ? String(row[col.field]) : '—')}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        );
                      }
                      return titleCol.render ? titleCol.render(row[titleCol.field], row) : (row[titleCol.field] != null ? String(row[titleCol.field]) : '—');
                    })()}
                  </Box>
                )}
                {/* Content columns – group by mobileSameLineGroup, then stacked */}
                {contentCols.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {(() => {
                      const inlineWithTitleCols = contentCols.filter((c) => c.mobileInlineWithTitle);
                      const restCols = contentCols.filter((c) => !c.mobileInlineWithTitle);
                      const sameLineGroupKeys = [...new Set(restCols.map((c) => c.mobileSameLineGroup).filter(Boolean))];
                      const groupedCols = restCols.filter((c) => c.mobileSameLineGroup);
                      const standaloneCols = restCols.filter((c) => !c.mobileSameLineGroup);

                      return (
                        <>
                          {sameLineGroupKeys.map((groupKey) => (
                            <Box
                              key={groupKey}
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 3,
                                alignItems: 'flex-start',
                                py: 0.75,
                                borderBottom: (standaloneCols.length > 0 || sameLineGroupKeys.indexOf(groupKey) < sameLineGroupKeys.length - 1)
                                  ? (isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f1f5f9')
                                  : 'none',
                              }}
                            >
                              {restCols.filter((c) => c.mobileSameLineGroup === groupKey).map((col) => (
                                <Box key={col.field} sx={{ minWidth: 0, pr: 1, textAlign: col.align || 'left', ...(col.align === 'right' && { marginLeft: 'auto' }) }}>
                                  <Typography sx={{ display: 'block', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: isDark ? '#64748b' : '#94a3b8', mb: 0.25 }}>{col.label}</Typography>
                                  <Box sx={{ fontSize: '0.875rem', color: isDark ? '#e2e8f0' : '#334155', lineHeight: 1.4 }}>
                                    {col.render ? col.render(row[col.field], row) : (row[col.field] != null ? String(row[col.field]) : '—')}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          ))}
                          {standaloneCols.map((col) => (
                            <Box
                              key={col.field}
                              sx={{
                                py: 0.75,
                                textAlign: col.align || 'left',
                                borderBottom: standaloneCols.indexOf(col) < standaloneCols.length - 1
                                  ? (isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f1f5f9')
                                  : 'none',
                              }}
                            >
                              <Typography sx={{ display: 'block', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: isDark ? '#64748b' : '#94a3b8', mb: 0.25 }}>{col.label}</Typography>
                              <Box sx={{ fontSize: '0.875rem', color: isDark ? '#e2e8f0' : '#334155', lineHeight: 1.4 }}>
                                {col.render ? col.render(row[col.field], row) : (row[col.field] != null ? String(row[col.field]) : '—')}
                              </Box>
                            </Box>
                          ))}
                        </>
                      );
                    })()}
                  </Box>
                )}
                {/* Actions – own row, right-aligned, no gap from content */}
                {actions && (
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'flex-end',
                      gap: 0.75,
                      alignItems: 'center',
                    }}
                  >
                    {typeof actions === 'function' ? actions(row) : (
                      <IconButton size="small" sx={{ color: isDark ? '#718096' : '#718096' }}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                )}
                {onRowClick && !actions && (
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <ChevronRightIcon sx={{ fontSize: 20, color: isDark ? '#64748b' : '#94a3b8' }} />
                  </Box>
                )}
              </Box>
            ))
          )}
        </Box>
        {pagination && !loading && data.length > 0 && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              borderTop: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #e2e8f0',
              backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
              color: isDark ? '#cbd5e1' : '#4a5568',
              '& .MuiTablePagination-selectLabel': { color: isDark ? '#cbd5e1' : '#4a5568', fontSize: '0.8125rem' },
              '& .MuiTablePagination-displayedRows': { color: isDark ? '#cbd5e1' : '#4a5568', fontSize: '0.8125rem' },
              '& .MuiTablePagination-select': { fontSize: '0.8125rem', color: isDark ? '#ffffff' : '#1a202c' },
              '& .MuiIconButton-root': {
                color: isDark ? '#718096' : '#718096',
              },
              '& .MuiIconButton-root:hover': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : '#f7fafc',
              },
              '& .MuiIconButton-root.Mui-disabled': {
                color: isDark ? '#475569' : '#cbd5e1',
              },
            }}
          />
        )}
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: '12px',
          backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
          boxShadow: 'none',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
          overflowX: 'auto',
          maxWidth: '100%',
        }}
      >
        <Table sx={{ minWidth: 0 }}>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < data.length}
                    checked={data.length > 0 && selected.length === data.length}
                    onChange={onSelectAll}
                    sx={{
                      color: isDark ? '#475569' : '#cbd5e1',
                      '&.Mui-checked': { color: isDark ? '#818cf8' : '#1976d2' },
                    }}
                  />
                </TableCell>
              )}
              {columns.map((column, index) => (
                <TableCell
                  key={index}
                  sx={{
                    width: column.width,
                    minWidth: column.minWidth,
                    padding: '12px 8px',
                  }}
                  align={column.align || 'left'}
                >
                  {column.label}
                </TableCell>
              ))}
              {actions && (
                <TableCell align="right">Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              renderLoadingRows()
            ) : data.length === 0 ? (
              renderEmptyState()
            ) : (
              data.map((row, rowIndex) => (
                <TableRow
                  key={row.id || rowIndex}
                  sx={{
                    transition: 'background-color 0.15s ease',
                    cursor: onRowClick ? 'pointer' : 'default',
                    '&:hover': {
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                    },
                    '&:last-child td': {
                      borderBottom: 'none',
                    },
                  }}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.includes(row.id)}
                        onChange={() => onSelectRow(row.id)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          color: isDark ? '#475569' : '#cbd5e1',
                          '&.Mui-checked': { color: isDark ? '#818cf8' : '#088164' },
                        }}
                      />
                    </TableCell>
                  )}
                  {columns.map((column, colIndex) => (
                    <TableCell
                      key={colIndex}
                      sx={{ padding: '12px 8px' }}
                      align={column.align || 'left'}
                    >
                      {column.render ? column.render(row[column.field], row) : row[column.field]}
                    </TableCell>
                  ))}
                  {actions && (
                    <TableCell sx={{ padding: '12px 8px' }} align="right">
                      {typeof actions === 'function' ? (
                        actions(row)
                      ) : (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          sx={{
                            color: isDark ? '#718096' : '#718096',
                            '&:hover': {
                              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f7fafc',
                              color: isDark ? '#ffffff' : '#1a202c',
                            },
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && !loading && data.length > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid #e2e8f0',
            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
            color: isDark ? '#cbd5e1' : '#4a5568',
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              color: isDark ? '#cbd5e1' : '#4a5568',
              fontSize: '0.875rem',
            },
            '.MuiTablePagination-select': {
              fontSize: '0.875rem',
              color: isDark ? '#ffffff' : '#1a202c',
            },
            '.MuiIconButton-root': {
              color: isDark ? '#718096' : '#718096',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f7fafc',
              },
              '&.Mui-disabled': {
                color: isDark ? '#475569' : '#cbd5e1',
              },
            },
          }}
        />
      )}
    </Box>
  );
};

export default DataTable;
